import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Edit } from "lucide-react";

export default function AdminPackages() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    const { data } = await supabase.from("packages").select("*").order("sort_order");
    setList(data || []);
  }
  useEffect(() => { load(); }, []);

  function newPkg() {
    setEditing({ slug: "", name: "", description: "", price_cents: 0, currency: "USD", billing_period: "monthly", features: [], jlpt_levels: [], active: true, highlight: false, sort_order: 0 });
    setOpen(true);
  }

  async function save() {
    if (!editing) return;
    const payload = {
      ...editing,
      features: typeof editing.features === "string" ? editing.features.split("\n").filter(Boolean) : editing.features,
      jlpt_levels: typeof editing.jlpt_levels === "string" ? editing.jlpt_levels.split(",").map((s: string) => s.trim()).filter(Boolean) : editing.jlpt_levels,
    };
    const { id, ...rest } = payload;
    const op = id ? supabase.from("packages").update(rest).eq("id", id) : supabase.from("packages").insert(rest);
    const { error } = await op;
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setOpen(false); load(); }
  }

  async function del(id: string) {
    if (!confirm("Delete this package?")) return;
    await supabase.from("packages").delete().eq("id", id);
    load();
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Packages</h2>
        <Button onClick={newPkg}>Add package</Button>
      </div>
      <div className="grid gap-3">
        {list.map((p) => (
          <Card key={p.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{p.name} <span className="text-xs text-muted-foreground">({p.slug})</span></div>
                <div className="text-sm text-muted-foreground">${(p.price_cents / 100).toFixed(2)} / {p.billing_period} {p.highlight && "• Popular"}</div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="ghost" onClick={() => { setEditing({ ...p, features: (p.features || []).join("\n"), jlpt_levels: (p.jlpt_levels || []).join(", ") }); setOpen(true); }}><Edit className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} package</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Price (cents)</Label><Input type="number" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: +e.target.value })} /></div>
                <div><Label>Currency</Label><Input value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} /></div>
                <div><Label>Period</Label>
                  <select className="w-full h-10 rounded-md border px-2 bg-background" value={editing.billing_period} onChange={(e) => setEditing({ ...editing, billing_period: e.target.value })}>
                    <option value="monthly">monthly</option><option value="yearly">yearly</option><option value="one_time">one_time</option><option value="lifetime">lifetime</option>
                  </select>
                </div>
              </div>
              <div><Label>Features (one per line)</Label><Textarea rows={4} value={editing.features} onChange={(e) => setEditing({ ...editing, features: e.target.value })} /></div>
              <div><Label>JLPT levels (comma-separated, e.g. N5, N4)</Label><Input value={editing.jlpt_levels} onChange={(e) => setEditing({ ...editing, jlpt_levels: e.target.value })} /></div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.highlight} onChange={(e) => setEditing({ ...editing, highlight: e.target.checked })} /> Highlight</label>
              </div>
              <Button onClick={save} className="w-full">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
